// Transport for RUM batches. Telemetry is BEST-EFFORT and must never throw into
// the app or block navigation (doctrine 1.5 — degrade gracefully in UX).
//
//  - `beacon` (web/admin, same-origin): `navigator.sendBeacon("/api/rum", blob)`.
//    The browser flushes it even as the page unloads and it rides the guest
//    cookie automatically. A relative path keeps it same-origin.
//  - `fetch` (seller, cross-origin CDN): `fetch(absoluteUrl, { keepalive: true,
//    credentials: "omit" })` — the CDN origin can't send a same-origin beacon, so
//    it POSTs cross-origin with no credentials (backend CORS-allowlists the origin).
//
// If a beacon is refused (the ~64KB user-agent queue is full) we fall back to a
// keepalive fetch so a full queue never silently drops the batch.

import type { ClientErrorBatchRequest, RumBatchRequest } from "./types";
import type { ClientFunnelEventBatchRequest } from "../analytics/types";

export type Transport = "beacon" | "fetch";

export function sendBatch(
  path: string,
  body: RumBatchRequest | ClientErrorBatchRequest | ClientFunnelEventBatchRequest,
  transport: Transport,
  endpoint: string,
  // Called when the payload did not land, so the batch is gone. Never called on
  // the happy path, and it must not throw. It covers the FETCH path only: a
  // `sendBeacon` the browser ACCEPTED is fire-and-forget, so a 422/429 there is
  // unobservable. With the `beacon` transport it fires only when `sendBeacon` is
  // missing or refuses (a full ~64KB queue) and the fetch fallback then fails.
  // Read it as "the transport was under pressure", not as an outage detector.
  onDropped?: (reason: string) => void,
): void {
  // Inside the guard, not before it. A throw here would escape into whatever
  // happened to trigger the flush — an unrelated `trackEvent`, or a `pagehide`
  // listener — which is the worst possible place to attribute it from, and
  // telemetry must never throw into the app (doctrine 1.5).
  let json: string;
  try {
    json = JSON.stringify(body);
  } catch {
    report(onDropped, "unserializable");
    return;
  }

  if (
    transport === "beacon" &&
    typeof navigator !== "undefined" &&
    typeof navigator.sendBeacon === "function"
  ) {
    // sendBeacon can throw (e.g. some privacy modes) — never let it reach the app.
    try {
      const queued = navigator.sendBeacon(path, new Blob([json], { type: "application/json" }));
      if (queued) return;
    } catch {
      /* fall through to fetch */
    }
  }

  // fetch path: absolute URL for the cross-origin `fetch` transport; relative for a
  // beacon fallback (which stays same-origin).
  const url = transport === "fetch" ? `${endpoint}${path}` : path;
  try {
    void fetch(url, {
      method: "POST",
      keepalive: true,
      credentials: transport === "fetch" ? "omit" : "same-origin",
      headers: { "content-type": "application/json" },
      body: json,
    })
      .then((response) => {
        // A 422 (one malformed item) or a 429 (rate limited) RESOLVES, so the
        // rejection handler below never sees it even though the batch is just as
        // gone. Both are exactly the cases batching made expensive.
        if (!response.ok) report(onDropped, `http_${response.status}`);
      })
      .catch(() => report(onDropped, "fetch_rejected"));
  } catch {
    /* best-effort telemetry — swallow so a failed send never breaks the page */
    report(onDropped, "fetch_threw");
  }
}

/** Invoke the drop callback without ever letting it reach the app (doctrine 1.5). */
function report(onDropped: ((reason: string) => void) | undefined, reason: string): void {
  try {
    onDropped?.(reason);
  } catch {
    /* a reporter that throws must not be worse than no reporter */
  }
}
